import type { I$W, TFunction } from '@wix/yoshi-flow-editor';
import { HEADER_WIDGET_COMPONENT_IDS } from '../../appConsts/blocksIds';
import { context } from '../../context/RootContext';
import type { BindAll, IHeaderController } from './types';
import { HeaderStore } from 'root/states/HeaderStore';
import {
  getCartShippingDetailsFromHeaderStore,
  STATUS_INDICATOR_COLORS,
  resolveTimeString,
  getOnDispatchTypeChange,
  openDispatchModal,
} from './headerUtils';
import { LiveSiteClickFulfillmentOrigin } from '@wix/restaurants-bi';
import type { Address } from 'root/types/businessTypes';
import { autorun, when } from 'mobx';
import { fulfillmentPickerStore } from 'root/states/FulfillmentPickerStore';
import { dispatchState } from 'root/states/DispatchState';
import { APP_DEF_IDS } from '@wix/restaurants-consts';

export class HeaderController implements IHeaderController {
  constructor(
    private $bindAll: BindAll,
    private $w: I$W,
    private t: TFunction,
    private addressFormatter: Function,
    private locale: string,
    private timezone: string,
    private isMemberLoggedIn: boolean | undefined,
    private isSSR: boolean,
    private isEditor: boolean,
    private supportMultiLocation: boolean
  ) {}

  init() {
    const headerStore = new HeaderStore(this.timezone, this.locale);

    when(
      () => !headerStore.isLoading,
      async () => {
        if (!this.isSSR && headerStore.isOpen && !this.isEditor) {
          const cart = await context.CartService?.getCurrentCart();
          const isCartContainsOrdersItems = cart?.lineItems?.some(
            (item) => item.catalogReference?.appId === APP_DEF_IDS.orders
          );
          // use cart location id if it exists and the cart has items, otherwise use selected location id
          const cartLocationId =
            isCartContainsOrdersItems && cart?.businessLocationId
              ? cart?.businessLocationId
              : undefined;
          const shippingDetails = getCartShippingDetailsFromHeaderStore(
            headerStore,
            cartLocationId
          );
          context.CartService?.setShippingDetails(shippingDetails);
        }
        headerStore.syncTimezone();

        context.initialTimeSlotText = resolveTimeString({
          asapOptions: {
            isASAP: headerStore.isASAP,
            exactTime: headerStore.asapTimeExact,
            timeRange: headerStore.asapTimeRange,
          },
          selectedTime: headerStore.selectedTime,
          t: this.t,
          timezone: headerStore.currentTimezone,
          dispatchType: headerStore.dispatchType,
        });
      }
    );

    autorun(() => {
      const isMultiLocation = this.supportMultiLocation && dispatchState.isMultiLocation;
      if (isMultiLocation !== headerStore.isMultiLocation) {
        headerStore.setIsMultiLocation(isMultiLocation);
      }
    });

    autorun(() => {
      const fulfillmentPicker = this.$w(HEADER_WIDGET_COMPONENT_IDS.fulfillmentPicker);
      if (!headerStore.isLoading) {
        const shouldBeDeleted =
          !headerStore.hasConfiguredDispatches ||
          (headerStore.isMultiLocation && !headerStore.isLocationSelected && !this.isEditor);

        if (!shouldBeDeleted) {
          if (fulfillmentPicker.deleted) {
            fulfillmentPicker.restore();
          }
        }

        if (shouldBeDeleted) {
          !fulfillmentPicker.deleted && fulfillmentPicker.delete && fulfillmentPicker.delete();
        }
      }
    });

    // Sync timezone when dispatch state timezone changes
    autorun(() => {
      // This autorun will trigger whenever dispatchState.timezone changes
      const currentTimezone = dispatchState.timezone;
      if (currentTimezone !== headerStore.currentTimezone) {
        headerStore.syncTimezone();
      }
    });

    const formatPrice = (price?: string) => {
      let formattedMin = '';
      if (typeof price !== 'undefined') {
        formattedMin = context.priceFormatter(Number(price));
      }
      return formattedMin;
    };

    const formatAddress = (address?: Address) => {
      let formattedAddress = '';
      if (address && this.addressFormatter) {
        formattedAddress = this.addressFormatter({ address }, { appendCountry: false })
          .filter((part: string) => part && /\S/.test(part))
          .join(', ');
      }
      return (
        (formattedAddress ||
          address?.formattedAddress ||
          address?.addressLine ||
          address?.streetAddress?.formattedAddressLine) ??
        ''
      );
    };

    autorun(() => {
      fulfillmentPickerStore.setObservableProps({
        selectedDispatchType: headerStore.dispatchType,
        onDispatchTypeChange: getOnDispatchTypeChange(headerStore, this.isMemberLoggedIn),
        hasPopup: true,
        shouldConsiderAllLocations: false,
      });
    });

    this.$bindAll({
      [HEADER_WIDGET_COMPONENT_IDS.badgesContainer]: {
        hidden: () => headerStore.isLoading,
        collapsed: () =>
          !this.isEditor && headerStore.isMultiLocation && !headerStore.isLocationSelected,
      },
      [HEADER_WIDGET_COMPONENT_IDS.locationNameWrapper]: {
        collapsed: () =>
          !(this.isEditor && headerStore.isMultiLocation) && !headerStore.isLocationSelected,
        hidden: () =>
          !(this.isEditor && headerStore.isMultiLocation) && !headerStore.isLocationSelected,
      },
      [HEADER_WIDGET_COMPONENT_IDS.locationName]: {
        label: () => headerStore.location?.name ?? '',
        onClick: () => {
          context.biReporterService?.reportOloLiveSiteClickOnFulfillmentBiEvent({
            origin: LiveSiteClickFulfillmentOrigin.START_ORDER,
            isMemberLoggedIn: this.isMemberLoggedIn,
          });
          openDispatchModal(headerStore, headerStore.dispatchState);
        },
        accessibility: {
          ariaAttributes: {
            label: () =>
              this.t('header_olo.locationName.ariaLabel', { name: headerStore.location?.name }),
          },
        },
      },
      [HEADER_WIDGET_COMPONENT_IDS.fulfillmentContainer]: {
        hidden: () => headerStore.isLoading,
        collapsed: () =>
          !this.isEditor && headerStore.isMultiLocation && !headerStore.isLocationSelected,
        deleted: () => !headerStore.isLoading && !headerStore.hasConfiguredDispatches,
      },
      [HEADER_WIDGET_COMPONENT_IDS.headerContainer]: {
        onViewportEnter: () => context.pubsub.publish('onHeaderViewportEnter'),
        onViewportLeave: () => context.pubsub.publish('onHeaderViewportLeave'),
      },
      [HEADER_WIDGET_COMPONENT_IDS.fulfillmentPicker]: {
        collapsed: () => headerStore.isLoading,
      },
      [HEADER_WIDGET_COMPONENT_IDS.acceptOrders]: {
        text: () =>
          headerStore.availableDispatchTypesForCurrentLocation.length
            ? this.t('header_olo.status.AcceptingOrders')
            : this.t('header_olo.status.NotAcceptingOrders'),
      },
      [HEADER_WIDGET_COMPONENT_IDS.statusIndicator]: {
        style: {
          backgroundColor: () =>
            headerStore.availableDispatchTypesForCurrentLocation.length
              ? STATUS_INDICATOR_COLORS.ONLINE
              : STATUS_INDICATOR_COLORS.OFFLINE,
        },
        collapsed: () => headerStore.isDataLoaded,
      },
      [HEADER_WIDGET_COMPONENT_IDS.minOrder]: {
        text: () => {
          return this.t('header_olo.minOrder.exact', {
            amount: formatPrice(headerStore.minOrder),
          });
        },
      },
      [HEADER_WIDGET_COMPONENT_IDS.minOrderWrapper]: {
        hidden: () => headerStore.isLoading,
        collapsed: () =>
          (!headerStore.minOrder || Number(headerStore.minOrder) === 0) && !headerStore.isLoading,
      },
      [HEADER_WIDGET_COMPONENT_IDS.deliveryFee]: {
        text: () => {
          return headerStore.deliveryFee === '0'
            ? this.t('header_olo.deliveryFee.free')
            : this.t('header_olo.deliveryFee.exact', {
                amount: formatPrice(headerStore.deliveryFee),
              });
        },
      },
      [HEADER_WIDGET_COMPONENT_IDS.deliveryFeeWrapper]: {
        hidden: () => headerStore.isLoading,
        collapsed: () => !headerStore.deliveryFee && !headerStore.isLoading,
      },
      [HEADER_WIDGET_COMPONENT_IDS.freeDelivery]: {
        collapsed: () => !headerStore.freeDispatchPriceThreshold || headerStore.deliveryFee === '0',
        text: () => {
          return headerStore.freeDispatchPriceThreshold === '0'
            ? this.t('header_olo.deliveryFee.free')
            : this.t('header_olo.deliveryFee.freeAbove', {
                amount: formatPrice(headerStore.freeDispatchPriceThreshold),
              });
        },
      },
      [HEADER_WIDGET_COMPONENT_IDS.freeDeliveryWrapper]: {
        collapsed: () => !headerStore.freeDispatchPriceThreshold,
      },
      [HEADER_WIDGET_COMPONENT_IDS.addressInfo]: {
        text: () => {
          const prefix = headerStore.isDelivery
            ? this.t('header_olo.address.delivery')
            : this.t('header_olo.address.pickup');
          const { selectedAddress } = headerStore;
          return prefix + formatAddress(selectedAddress);
        },
      },
      [HEADER_WIDGET_COMPONENT_IDS.addressInfoLine]: {
        hidden: () => headerStore.isLoading,
        collapsed: () =>
          (!headerStore.hasConfiguredDispatches ||
            (headerStore.onlyDeliveryConfiguredForCurrentLocation &&
              !headerStore.availableDispatchTypesForCurrentLocation.length)) &&
          !headerStore.isLoading,
      },
      [HEADER_WIDGET_COMPONENT_IDS.timeInfo]: {
        text: () => {
          const text = resolveTimeString({
            asapOptions: {
              isASAP: headerStore.startsNow,
              exactTime: headerStore.asapTimeExact,
              timeRange: headerStore.asapTimeRange,
            },
            selectedTime: headerStore.selectedTime,
            t: this.t,
            timezone: headerStore.currentTimezone,
            dispatchType: headerStore.dispatchType,
          });
          return text;
        },
      },
      [HEADER_WIDGET_COMPONENT_IDS.timeInfoLine]: {
        collapsed: () =>
          !headerStore.isCurrentDispatchAvailable ||
          (!headerStore.hasDispatchTime && !headerStore.shouldShowTimeInfo),
      },
      [HEADER_WIDGET_COMPONENT_IDS.addressChange]: {
        onClick: () => {
          context.biReporterService?.reportOloLiveSiteClickOnFulfillmentBiEvent({
            origin: LiveSiteClickFulfillmentOrigin.CHANGE_ADDRESS,
            isMemberLoggedIn: this.isMemberLoggedIn,
          });
          openDispatchModal(headerStore, headerStore.dispatchState);
        },
        label: () => {
          return headerStore.selectedAddress
            ? this.t('header_olo.address.delivery.change')
            : this.t('header_olo.address.delivery.add');
        },
        accessibility: {
          ariaAttributes: {
            describedBy: () => this.$w(HEADER_WIDGET_COMPONENT_IDS.addressInfo),
          },
        },
        collapsed: () => !headerStore.shouldShowAddressChange,
      },
      [HEADER_WIDGET_COMPONENT_IDS.timeChange]: {
        onClick: () => {
          context.biReporterService?.reportOloLiveSiteClickOnFulfillmentBiEvent({
            origin: LiveSiteClickFulfillmentOrigin.CHANGE_TIME,
            isMemberLoggedIn: this.isMemberLoggedIn,
          });
          openDispatchModal(headerStore, headerStore.dispatchState);
        },
        label: () => this.t('header_olo.time.change'),
        collapsed: () => headerStore.isASAP || !headerStore.shouldShowTimeInfo,
        accessibility: {
          ariaAttributes: {
            describedBy: () => this.$w(HEADER_WIDGET_COMPONENT_IDS.timeInfo),
          },
        },
      },
    });
  }

  initHeaderTexts({
    t,
    headerTitle,
    headerDescription,
  }: {
    t: TFunction;
    headerTitle: string;
    headerDescription: string;
  }) {
    this.$bindAll({
      [HEADER_WIDGET_COMPONENT_IDS.headerTitle]: {
        text: () => {
          return headerTitle || t('header_olo.default.title');
        },
      },
      [HEADER_WIDGET_COMPONENT_IDS.headerDescription]: {
        text: () => headerDescription || t('header_olo.default.description'),
      },
    });
  }
}
